import { h } from 'vue'
import { RouterLink } from 'vue-router'
import get from 'lodash/get'
import trimEnd from 'lodash/trimEnd'
import { default as _join } from 'lodash/join'

function joinAndNormalize(args, sep) {
  return trimEnd(_join(args, sep).replace(/[/]+/g, '/'), '/')
}

function extractMajorVersionFromRoute(routeName) {
  const match = routeName.match(/^(five|six|seven|latest)\./)
  if (!match) return null
  const versionMap = { five: 5, six: 6, seven: 7, latest: 7 }
  return versionMap[match[1]]
}

// Router 4 changed `resolve`: it takes the target alone and returns the resolved
// route directly, where Router 3 took (to, current, append) and wrapped the
// result as `{ route, href, location, resolved }`. Reading `res.resolved` threw
// on every render, which is what left the app unmounted.
//
// The Router 3 shape is rebuilt here rather than at the eight places downstream
// that read `resolution.route` / `.href` / `.location`. `append` is gone from
// Router 4 and no caller ever passed it.
function resolve({ to, current, routers }) {
  for (const router of routers) {
    if (!router) continue

    // Router 4 throws on an unknown *name*, where Router 3 returned a
    // resolution with an empty `matched`. A miss here is not an error: it means
    // this router does not own the target, and the next one should be tried.
    // Letting it propagate tore down the whole app on any page holding a
    // <site-link> to a name outside its own router — a blank page, not a broken
    // link.
    let resolved
    try {
      resolved = router.resolve(to, current)
    } catch (e) {
      continue
    }

    if (resolved.matched.length > 0) {
      return { resolution: { route: resolved, href: resolved.href, location: resolved }, router }
    }
  }

  return { resolution: {}, router: null }
}

function guardEvent(e) {
  // don't redirect with control keys
  if (e.metaKey || e.altKey || e.ctrlKey || e.shiftKey) {
    return
  }
  // don't redirect when preventDefault called
  if (e.defaultPrevented) {
    return
  }
  // don't redirect on right click
  if (e.button !== undefined && e.button !== 0) {
    return
  }
  // don't redirect if `target="_blank"`
  if (e.currentTarget && e.currentTarget.getAttribute) {
    var target = e.currentTarget.getAttribute('target')
    if (/\b_blank\b/i.test(target)) {
      return
    }
  }
  // this may be a Weex event which doesn't have this method
  if (e.preventDefault) {
    e.preventDefault()
  }
  return true
}

export default function (vue, params) {
  const { defaultLayout, fallbackRouter } = params || {}

  // Vue 3 has no `Vue.options`, so RouterLink is imported from vue-router
  // directly rather than read off the global constructor's component registry.
  if (!RouterLink) {
    throw new Error("Couldn't find the RouterLink from vue-router!")
  }

  if (!defaultLayout) {
    throw new Error('A defaultLayout is required when registering the SiteLink component')
  }

  return {
    name: 'SiteLink',

    extends: RouterLink,

    render() {
      const current = this.$route
      const isExternal = typeof this.to === 'string' && this.to.startsWith('http')

      const { resolution, router: matchingRouter } = resolve({
        to: this.to,
        current,
        routers: [this.$router, fallbackRouter]
      })

      const currentVersion = extractMajorVersionFromRoute(get(current, 'name', ''))
      const targetVersion = extractMajorVersionFromRoute(get(this.to, 'name', ''))
      const movingAcrossMajorVersion = currentVersion !== null && targetVersion !== null && currentVersion !== targetVersion

      const usedFallback = matchingRouter === fallbackRouter

      const layoutsDiffer = get(current, ['meta', 'layout'], defaultLayout) !== get(resolution.route, ['meta', 'layout'], defaultLayout)
      const resolvedName = get(resolution.route, ['name'], '')

      // Whether this navigation must bypass the router and do a full page load.
      // Vue 2 expressed this by deleting the click handler off RouterLink's
      // captured render arguments; the anchor is built here now, so it is a flag.
      // `matchingRouter === null` means NO router owns the target. A path always
      // matches the catch-all, so this is reached only by an unknown route NAME,
      // which Router 4's `resolve()` throws on. Without it here the anchor below
      // still gets an onClick closing over a null router, and clicking throws.
      const bypassRouter =
        matchingRouter === null ||
        movingAcrossMajorVersion ||
        layoutsDiffer ||
        usedFallback ||
        isExternal ||
        resolvedName === 'not.found'

      // Router 4 declares these props but gives them no default — the fallback
      // moved inside `useLink()`, which this component does not go through
      // because it builds its own anchor. Undefined here became the literal
      // class `"undefined"` on every link that did not pass one. Resolved the
      // way Router 4 resolves it, so a configured `linkActiveClass` still wins
      // and the emitted classes match what Router 3 produced.
      // `matchingRouter` is null when no router owns the target — the fallback
      // path that ends in a full page load — so read the options off this
      // component's own router, which is always present.
      const routerOptions = (matchingRouter || this.$router).options
      const activeClass = this.activeClass ?? routerOptions.linkActiveClass ?? 'router-link-active'
      const exactActiveClass = this.exactActiveClass ?? routerOptions.linkExactActiveClass ?? 'router-link-exact-active'

      const currentPath = joinAndNormalize([this.$router.options.history.base, current.path], '/')
      const toPath = joinAndNormalize([resolution.href])

      const isActive = currentPath.indexOf(toPath) > -1
      const isExactActive = currentPath === toPath

      if (this.custom && this.$slots.default) {
        const shouldReplace = this.replace

        var handler = function (e) {
          if (guardEvent(e)) {
            // No router owns the target and there is no href to fall back to,
            // so there is nothing to navigate to — do nothing rather than throw.
            if (matchingRouter === null) return

            if (layoutsDiffer || usedFallback) {
              window.location.assign(resolution.href)
            } else {
              if (shouldReplace) {
                matchingRouter.replace(resolution.location).catch(() => {})
              } else {
                matchingRouter.push(resolution.location).catch(() => {})
              }
            }
          }
        }

        const scopedSlot = this.$slots.default({
          href: resolution.href,
          route: resolution.route,
          navigate: handler,
          isActive,
          isExactActive
        })

        if (!scopedSlot || !scopedSlot.length === 1) {
          throw new Error('A scoped slot must have exactly one child in it')
        }

        return scopedSlot[0]
      }

      // Router 4's RouterLink is a `setup()` component with no borrowable
      // render, so the anchor is built directly. Everything it needs — href,
      // active state, and whether to navigate in-app — is already resolved.
      const anchorProps = {
        href: isExternal ? this.to : resolution.href,
        class: {
          [activeClass]: isActive,
          [exactActiveClass]: isExactActive
        }
      }

      if (isExternal) {
        anchorProps.rel = 'noopener'
      }

      // Only an in-app navigation gets a click handler. Everything `bypassRouter`
      // covers — a different layout, the fallback router, an external URL, the
      // catch-all `not.found`, or no owning router at all — needs a full page
      // load, which is what a bare anchor does.
      if (!bypassRouter) {
        anchorProps.onClick = (event) => {
          if (!guardEvent(event)) return

          const target = this.replace ? matchingRouter.replace : matchingRouter.push

          target.call(matchingRouter, resolution.location).catch(() => {})
        }
      }

      return h('a', anchorProps, this.$slots.default ? this.$slots.default() : [])
    }
  }
}
